/**
 * `roles.administer`: the one permission the Roles & Permissions feature opens on (RBAC FR-0, FR-1).
 *
 * ⚠️ THE DATABASE IS THE ENFORCEMENT; THIS IS THE CATALOGUE AND THE COURTESY.
 *
 * The row and its grants are created by `20261101a3_rbac_1_6_seed_roles_administer.sql`.
 * `rolesAdministration.test.ts` reads that migration and fails if the description or the seeded
 * roles here disagree with it, the same arrangement `fieldPermissions.ts` has with KNK-0.5.
 *
 * ⚠️ `manage_roles` IS NOT THIS. It is read in 44 places across 39 files as a loose "is an admin"
 * proxy (booking links, chat, theme, drag-and-drop) and grants nothing in this feature. Gate the
 * roles screen on `canAdministerRoles()`, never on `manage_roles`.
 */

export const ROLES_ADMINISTER_KEY = 'roles.administer';

const SYSTEM_OVERRIDE = 'system.override';

export const ROLES_ADMINISTER = {
  key: ROLES_ADMINISTER_KEY,
  area: 'Administration',
  /** What an admin reads on the role editor row. Never the key. */
  label: 'Administer roles and permissions',
  /** One line, written for an admin. Also stored as `permissions.description`. */
  description:
    "Create, edit and delete this account's roles, choose what each one can do, and change who holds them.",
  /** Rendered in the role-change diff when the permission is added. */
  gained: 'Can open Roles & Permissions and change what every role in this account can do.',
  /** Rendered in the role-change diff when the permission is removed. */
  lost: 'Can no longer open Roles & Permissions or change any role.',
} as const;

/** The global roles the migration grants it to, in every account. */
export const ROLES_ADMINISTER_SEEDED_ROLES: readonly { id: number; name: string }[] = [
  { id: 7, name: 'Admin' },
  { id: 74, name: 'Agency Admin' },
];

/**
 * Whether the held permissions open the roles screen. `system.override` satisfies it, as it does
 * in `cost_holds_permission()`. `manage_roles` does not.
 */
export function canAdministerRoles(held: Iterable<string> | null | undefined): boolean {
  if (!held) return false;
  for (const p of held) {
    if (p === ROLES_ADMINISTER_KEY || p === SYSTEM_OVERRIDE) return true;
  }
  return false;
}
